import { useState } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Zoom,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PeopleIcon from '@mui/icons-material/People';
import WarningIcon from '@mui/icons-material/Warning';
import PaymentsIcon from '@mui/icons-material/Payments';
import InsightsIcon from '@mui/icons-material/Insights';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BadgeIcon from '@mui/icons-material/Badge';
import WorkIcon from '@mui/icons-material/Work';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { ToggleableRole, FeatureName, RoleFeatures } from '../../types';

interface FeatureItem {
  key: FeatureName;
  label: string;
  icon: React.ReactNode;
  description: string;
}

interface RoleTabConfig {
  role: ToggleableRole;
  label: string;
  icon: React.ReactNode;
  color: string;
}

const featureItems: FeatureItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, description: 'Main dashboard overview' },
  { key: 'trades', label: 'Trades', icon: <ShowChartIcon />, description: 'Trading operations' },
  { key: 'clients', label: 'Clients', icon: <PeopleIcon />, description: 'Client management' },
  { key: 'riskMonitor', label: 'Risk Monitor', icon: <WarningIcon />, description: 'Risk monitoring dashboard' },
  { key: 'payouts', label: 'Payouts', icon: <PaymentsIcon />, description: 'Payout management' },
  { key: 'analytics', label: 'Analytics', icon: <InsightsIcon />, description: 'Analytics and reports' },
];

const roleTabs: RoleTabConfig[] = [
  { role: 'admin', label: 'Admin', icon: <AdminPanelSettingsIcon />, color: '#8B5CF6' },
  { role: 'hr', label: 'HR', icon: <BadgeIcon />, color: '#EC4899' },
  { role: 'employee', label: 'Employee', icon: <WorkIcon />, color: '#10B981' },
];

export function OmniButton() {
  const [open, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const { isSuperAdmin } = useAuth();
  const {
    isLoading,
    error,
    toggleAllFeatures,
    toggleRoleFeatures,
    toggleFeature,
    getFeaturesForRole,
  } = useFeatureFlags();

  // Only show for Super Admin
  if (!isSuperAdmin) {
    return null;
  }

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleToggleAll = async (enabled: boolean) => {
    await toggleAllFeatures(enabled);
  };

  const handleToggleRoleFeatures = async (role: ToggleableRole, enabled: boolean) => {
    await toggleRoleFeatures(role, enabled);
  };

  const handleToggleFeature = async (role: ToggleableRole, feature: FeatureName) => {
    const roleFeatures = getFeaturesForRole(role);
    await toggleFeature(role, feature, !roleFeatures[feature]);
  };

  // Check if all features are enabled for a role
  const areAllRoleFeaturesEnabled = (role: ToggleableRole): boolean => {
    const roleFeatures = getFeaturesForRole(role);
    return featureItems.every((item) => roleFeatures[item.key]);
  };

  // Check if all features are enabled for all roles
  const areAllFeaturesEnabled = (): boolean => {
    return roleTabs.every((tab) => areAllRoleFeaturesEnabled(tab.role));
  };

  // Count enabled features for a role
  const getEnabledCount = (role: ToggleableRole): number => {
    const roleFeatures = getFeaturesForRole(role);
    return featureItems.filter((item) => roleFeatures[item.key]).length;
  };

  const currentRole = roleTabs[selectedTab];
  const currentRoleFeatures: RoleFeatures = getFeaturesForRole(currentRole.role);
  const allEnabled = areAllFeaturesEnabled();

  return (
    <>
      {/* Floating Action Button */}
      <Tooltip title="Feature Controls (Super Admin)" placement="left">
        <Zoom in={true}>
          <Fab
            onClick={handleOpen}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              bgcolor: '#0A0A0A',
              color: '#FFFFFF',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                bgcolor: '#262626',
              },
              zIndex: 1000,
            }}
          >
            <SettingsIcon />
          </Fab>
        </Zoom>
      </Tooltip>

      {/* Feature Control Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: 600,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#0A0A0A',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <PowerSettingsNewIcon />
          Role-Based Feature Control
        </DialogTitle>

        <DialogContent sx={{ pt: 3, px: 0 }}>
          {error && (
            <Alert severity="error" sx={{ mx: 3, mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Global Master Toggle */}
          <Box
            sx={{
              p: 2,
              mx: 3,
              mb: 3,
              borderRadius: 2,
              bgcolor: allEnabled ? '#ECFDF5' : '#FEF2F2',
              border: `1px solid ${allEnabled ? '#A7F3D0' : '#FECACA'}`,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={allEnabled}
                  onChange={() => handleToggleAll(!allEnabled)}
                  disabled={isLoading}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#10B981',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#10B981',
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {allEnabled ? 'All Features Enabled (All Roles)' : 'Enable All Features (All Roles)'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Master toggle to enable or disable all features for all roles
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Role Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 64,
                },
              }}
            >
              {roleTabs.map((tab) => (
                <Tab
                  key={tab.role}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: tab.color }}>{tab.icon}</Box>
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {tab.label}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${getEnabledCount(tab.role)}/${featureItems.length}`}
                          sx={{
                            height: 18,
                            fontSize: '0.7rem',
                            bgcolor: areAllRoleFeaturesEnabled(tab.role) ? '#D1FAE5' : '#FEE2E2',
                            color: areAllRoleFeaturesEnabled(tab.role) ? '#065F46' : '#991B1B',
                          }}
                        />
                      </Box>
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* Role-specific Controls */}
          <Box sx={{ px: 3, pt: 2 }}>
            {/* Role Master Toggle */}
            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                bgcolor: areAllRoleFeaturesEnabled(currentRole.role) ? '#F0FDF4' : '#FFF7ED',
                border: `1px solid ${areAllRoleFeaturesEnabled(currentRole.role) ? '#BBF7D0' : '#FED7AA'}`,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={areAllRoleFeaturesEnabled(currentRole.role)}
                    onChange={() => handleToggleRoleFeatures(currentRole.role, !areAllRoleFeaturesEnabled(currentRole.role))}
                    disabled={isLoading}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: currentRole.color,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: currentRole.color,
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {areAllRoleFeaturesEnabled(currentRole.role)
                        ? `All Features Enabled for ${currentRole.label}`
                        : `Enable All Features for ${currentRole.label}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Toggle all features for {currentRole.label.toLowerCase()} role
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Individual Feature Toggles */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {currentRole.label} Feature Controls
            </Typography>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ py: 0 }}>
                {featureItems.map((item) => (
                  <ListItem
                    key={item.key}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: currentRoleFeatures[item.key] ? '#F0FDF4' : '#FFF7ED',
                      border: `1px solid ${currentRoleFeatures[item.key] ? '#BBF7D0' : '#FED7AA'}`,
                    }}
                  >
                    <Box sx={{ mr: 2, color: currentRoleFeatures[item.key] ? currentRole.color : '#9CA3AF' }}>
                      {item.icon}
                    </Box>
                    <ListItemText
                      primary={item.label}
                      secondary={item.description}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={currentRoleFeatures[item.key]}
                        onChange={() => handleToggleFeature(currentRole.role, item.key)}
                        disabled={isLoading}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: currentRole.color,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: currentRole.color,
                          },
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {/* Info Note */}
          <Alert severity="info" sx={{ mx: 3, mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> HR features (Employees, Attendance, Salary) are always enabled
              for HR role and not affected by these toggles. Super Admin always has full access.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default OmniButton;
